/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Breadcrumb, Button, Col, Form, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './IdentificationTagScreen.module.scss';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import CompaniesService from 'api/companies/CompaniesService';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import DriversService from 'api/drivers/DriversService';
import { IdentificationTagDto } from 'api/identificationTags/models/IdentificationTagDto';
import IdentificationTagsService from 'api/identificationTags/IdentificationTagsService';

type Props = {
};

const IdentificationTagScreen: React.FC<Props> = () => {
    const { id, type } = useParams<{ id: string, type: string }>();
    const [isDetails, setIsDetails] = useState<boolean>(type === 'details');
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasIdentificationTagsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['CONFIG_IDENTIFICATION_TAGS_WRITE']);
    const hasMyIdentificationTagsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['MY_IDENTIFICATION_TAGS_WRITE']);
    const hasMyIdentificationTagsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['MY_IDENTIFICATION_TAGS_READ']);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<IdentificationTagDto | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [item, setItem] = useState<IdentificationTagDto>();
    const [companies, setCompanies] = useState<SelectValueLabel[]>();
    const [drivers, setDrivers] = useState<SelectValueLabel[]>();
    const [refresh, setRefresh] = useState<number>(1);
    const [formData, setFormData] = useState({
        id: '',
        code: '',
        companyId: '',
        driverId: '',
        rowVersion: ''
    });

    const getData = async () => {
        try {
            let result: any = { id: '', code: '' };
            Loading.show();
            const companies = await CompaniesService.getCompanies();
            setCompanies(companies);
            const drivers = await DriversService.getDrivers();
            setDrivers(drivers);
            if (id) {
                result = await IdentificationTagsService.getById(id);

                console.log(result)
            }
            setItem(result);
            setFormData(result);
            setRefresh(refresh + 1);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get identification tags information', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    };

    useEffect(() => {
        getData().catch(console.error);
    }, [id, type]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFormData({ ...formData, [key]: value });
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/identificationtags/${typeUrl}/${id}`);
            setIsDetails(typeUrl === 'details');
        } else {
            navigate('/identificationtags');
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }

        try {
            await IdentificationTagsService.remove(itemToRemove);
            onCancelRemove();
            navigateTo();
            toast.success(t('messages.record_delete_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete identification tags', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const showRemoveItemDialog = async (item: IdentificationTagDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (form.checkValidity() === false) {
            return;
        }
        try {
            Loading.show();
            const model: IdentificationTagDto = {
                id: formData.id,
                code: formData.code,
                companyId: formData.companyId,
                driverId: formData.driverId,
                rowVersion: formData.rowVersion,
            };
            if (model && model.id) {
                await IdentificationTagsService.update(model)
                navigateTo('details', id);
            } else if (model) {
                const id = await IdentificationTagsService.create(model)
                navigateTo('details', id);
            }
            Loading.hide();
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            if (error?.response?.status === 409) {
                toast(t('messages.simCard_already_exists'));
            } else {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the simCard with id: ${form.id}`, error);
                toast.error(t('messages.error_load_info'));
            }
            Loading.hide();
        }
    };

    return (
        <ScreenTitle title={t('identification_tags.title')}>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => { navigateTo() }}>
                    {t('identification_tags.title')}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{t(`common.${type}`)}</Breadcrumb.Item>
            </Breadcrumb>

            <Form onSubmit={onSubmit} noValidate validated={validated} key={refresh}>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="code">
                            <Form.Label>{t('identification_tags.code')}{!(isDetails || !hasIdentificationTagsWritePolicy) ? '*' : ''}</Form.Label>
                            <Form.Control required name="code" type="text" defaultValue={item?.code} readOnly={isDetails || !hasIdentificationTagsWritePolicy} onChange={handleChange} placeholder={t('identification_tags.code')} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="companyId">
                            <Form.Label>{t('identification_tags.company')}</Form.Label>
                            <Form.Select name="companyId" defaultValue={item?.companyId} disabled={(isDetails || !hasIdentificationTagsWritePolicy)}
                                onChange={(e: any) => { handleChange(e); }}>
                                <option value={''}>{t('common.select_option')}</option>
                                { companies &&
                                    companies.map(c => <option key={c.value} value={c.value}>{c.label}</option>)
                                }
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                { (hasMyIdentificationTagsWritePolicy || hasMyIdentificationTagsReadPolicy) &&
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="driverId">
                            <Form.Label>{t('identification_tags.drivers')}</Form.Label>
                            <Form.Select name="driverId" defaultValue={item?.driverId} disabled={isDetails}
                                onChange={(e: any) => { handleChange(e); }}>
                                <option value={''}>{t('common.select_option')}</option>
                                { drivers &&
                                    drivers.map(c => <option key={c.value} value={c.value}>{c.label}</option>)
                                }
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>}
                <div className={styles.buttonsContainer}>
                    <Button variant="secondary" type="button" className={styles.button} onClick={() => navigateTo()}>
                        {t('common.cancel')}
                    </Button>
                    {isDetails && hasIdentificationTagsWritePolicy &&
                        <Button variant="danger" type="button" className={styles.button} onClick={() => showRemoveItemDialog(item as IdentificationTagDto)} >
                            {t('common.delete')}
                        </Button>
                    }
                    {isDetails && (hasIdentificationTagsWritePolicy || hasMyIdentificationTagsWritePolicy) &&
                        <Button variant="warning" type="button" className={styles.button} onClick={() => navigateTo('edit', item?.id) }>
                            {t('common.edit')}
                        </Button>
                    }
                    {!isDetails && (hasIdentificationTagsWritePolicy || hasMyIdentificationTagsWritePolicy) &&
                        <Button variant="primary" type="submit" className={styles.button}>
                            {t('common.save')}
                        </Button>
                    }
                </div>
            </Form>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: item?.code ?? '' })} />
        </ScreenTitle>
    );
};

export default IdentificationTagScreen;
